import { get } from "lodash";

const scopedCaches = new Set();
let currentScope = null;

const DATE_FIELD_SUFFIX = /(birthDate|issuanceDate|effectiveDate)$/;
// Plan, duration and headcount live in the cache key of each tariff, not
// here: clicking Solo/Duo/Famille must reuse the visit cache. Codes and
// dates still move the fingerprint so a discount or effect date never
// keeps a stale amount.
const PRICING_FIELDS = ["partnerCode", "discountCode"];

const personShape = (person) => [
  get(person, "id") || "",
  get(person, "relationship") || "",
  get(person, "identity.user.birthDate") || get(person, "birthDate") || "",
];

// A buffered collection (children, related persons…) is still an insured
// person for the pricer, so it has to move the fingerprint before it is
// flushed to its entity.
const bufferedCollections = (fields) =>
  Object.keys(fields)
    .sort()
    .filter((key) => Array.isArray(fields[key]))
    .map((key) => [key, fields[key].map(personShape)]);

const pendingDates = (fields) =>
  Object.keys(fields)
    .sort()
    .filter((key) => DATE_FIELD_SUFFIX.test(key))
    .map((key) => [key, fields[key] || ""]);

/**
 * Everything the pricer reads: insured people, their birth dates, the effect
 * date and the codes that can discount the tariff. Fields written back by the
 * compute itself (quoted premium…) stay out so a stored tariff never
 * invalidates its own cache entry.
 */
export const pricingFingerprint = (project) => {
  const fields = get(project, "fields") || {};
  return JSON.stringify([
    get(project, "contact.user.birthDate") || "",
    (get(project, "contact.relatedPersons") || []).map(personShape),
    get(project, "contract.issuanceDate") || "",
    PRICING_FIELDS.map((key) => [key, fields[key] ?? ""]),
    bufferedCollections(fields),
    pendingDates(fields),
  ]);
};

export const priceScopeKey = ({ screenVisit, project }) =>
  JSON.stringify([
    screenVisit || 0,
    get(project, "id") || "",
    pricingFingerprint(project),
  ]);

export const registerScopedPriceCache = (cache) => {
  scopedCaches.add(cache);
  return cache;
};

const clearScopedCaches = () => {
  for (const cache of scopedCaches) cache.clear();
};

export const currentPriceCacheScope = () => currentScope;

export const syncPriceCacheScope = (scope) => {
  if (scope === currentScope) return false;
  currentScope = scope;
  clearScopedCaches();
  return true;
};

export const resetPriceCacheScope = () => {
  currentScope = null;
  clearScopedCaches();
};
